import { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import './SimulatorPreview.css';

interface PlayerPosition {
  x: number;
  y: number;
}

/** Where the loop is frozen for someone who does not want motion — mid-play, so the routes read. */
const STATIC_FRAME = 36;

function SimulatorPreview() {
  const [frame, setFrame] = useState(0);
  const totalFrames = 60;

  /**
   * Respect `prefers-reduced-motion`.
   *
   * This is the app's only animation that starts BY ITSELF and runs indefinitely — a 50ms interval
   * looping forever on the homepage, with no way to pause it. The main simulator is user-initiated
   * and short, so it is a different case; this one is the genuine reduced-motion gap, and the CSS
   * `prefers-reduced-motion` block could never have caught it because the movement comes from a JS
   * timer rather than a CSS animation.
   *
   * Reduced motion gets a single held frame instead of a still snap at kickoff, so the picture still
   * shows what the product does.
   */
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      setFrame(STATIC_FRAME);
      return undefined;
    }

    const interval = setInterval(() => {
      setFrame((f) => (f + 1) % totalFrames);
    }, 50);
    return () => clearInterval(interval);
  }, [prefersReducedMotion]);

  const progress = frame / totalFrames;

  // WR1 runs a post route (left side)
  const wr1: PlayerPosition = {
    x: 20 + progress * 25,
    y: 70 - progress * 45,
  };

  // WR2 runs a slant (left side, set back)
  const wr2: PlayerPosition = {
    x: 30 + progress * 20,
    y: 75 - progress * 30,
  };

  // WR3 runs an out route (right side)
  const wr3: PlayerPosition = {
    x: 70 - progress * 15,
    y: 75 - progress * 20,
  };

  // WR4 runs a go route (right side)
  const wr4: PlayerPosition = {
    x: 80,
    y: 70 - progress * 45,
  };

  // CB1 follows WR1
  const cb1: PlayerPosition = {
    x: 25 + progress * 20,
    y: 55 - progress * 35,
  };

  // CB2 follows WR4
  const cb2: PlayerPosition = {
    x: 75,
    y: 55 - progress * 40,
  };

  // Safety drops back
  const fs: PlayerPosition = {
    x: 40 - progress * 5,
    y: 35 - progress * 10,
  };

  const ss: PlayerPosition = {
    x: 60 + progress * 5,
    y: 35 - progress * 10,
  };

  return (
    <div className="simulator-preview">
      <div className="preview-field">
        {/* Field lines */}
        <div className="preview-yard-line" style={{ top: '25%' }} />
        <div className="preview-yard-line" style={{ top: '50%' }} />
        <div className="preview-los" />

        {/* Offense */}
        <div className="preview-player offense wr" style={{ left: `${wr1.x}%`, top: `${wr1.y}%` }}>
          <span>×</span>
        </div>
        <div className="preview-player offense wr" style={{ left: `${wr2.x}%`, top: `${wr2.y}%` }}>
          <span>×</span>
        </div>
        <div className="preview-player offense wr" style={{ left: `${wr3.x}%`, top: `${wr3.y}%` }}>
          <span>×</span>
        </div>
        <div className="preview-player offense wr" style={{ left: `${wr4.x}%`, top: `${wr4.y}%` }}>
          <span>×</span>
        </div>
        <div className="preview-player offense" style={{ left: '50%', top: '85%' }}>
          <span>□</span>
        </div>
        <div className="preview-player offense" style={{ left: '50%', top: '92%' }}>
          <span>×</span>
        </div>

        {/* Defense */}
        <div className="preview-player defense" style={{ left: `${cb1.x}%`, top: `${cb1.y}%` }}>
          <span>○</span>
        </div>
        <div className="preview-player defense" style={{ left: `${cb2.x}%`, top: `${cb2.y}%` }}>
          <span>○</span>
        </div>
        <div className="preview-player defense" style={{ left: `${fs.x}%`, top: `${fs.y}%` }}>
          <span>○</span>
        </div>
        <div className="preview-player defense" style={{ left: `${ss.x}%`, top: `${ss.y}%` }}>
          <span>○</span>
        </div>
        <div className="preview-player defense" style={{ left: '50%', top: '60%' }}>
          <span>○</span>
        </div>
      </div>
    </div>
  );
}

export default SimulatorPreview;
